import React, { useEffect } from 'react';
import { LogOut, LayoutDashboard, PlusCircle, BarChart3, Wrench, Wallet, Users, Map as MapIcon, Sun, X, GraduationCap } from 'lucide-react';
import NavItem from '../components/NavItem';
import { UserRole } from '../types';

export default function Sidebar({ 
  currentView, 
  onViewChange, 
  currentUser, 
  onLogout, 
  isMobileMenuOpen, 
  onCloseMobile,
  isCollapsed = false,
  onToggleCollapse
}) {
  const isOwnerOrAdmin = currentUser?.role === UserRole.ADMIN || currentUser?.role === UserRole.OWNER;
  const isEmployee = currentUser?.role === UserRole.EMPLOYEE;
  const go = (nextView) => {
    onViewChange(nextView);
    if (typeof onCloseMobile === 'function') onCloseMobile();
  };

  // Suporte para navegação por teclado
  useEffect(() => {
    if (!isMobileMenuOpen) return;
    
    const handleEscape = (e) => {
      if (e.key === 'Escape' && typeof onCloseMobile === 'function') {
        onCloseMobile();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isMobileMenuOpen, onCloseMobile]);

  return (
    <aside 
      className={`fixed inset-y-0 left-0 bg-white border-r border-slate-200 flex flex-col h-full z-50 transition-all duration-200 w-72 overflow-visible ${
        isCollapsed ? 'lg:w-14' : ''
      } ${
        isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      }`}
      aria-label="Navegação principal"
    >
      <div className={`p-4 lg:p-3 flex items-center justify-between gap-2 ${isCollapsed ? 'lg:justify-center lg:px-0' : ''}`}>
        <div className={`flex items-center gap-3 flex-1 min-w-0 ${isCollapsed ? 'lg:hidden' : ''}`}>
          <div className="w-12 h-12 bg-[#1b5e20] rounded-xl flex items-center justify-center shadow-lg shrink-0">
            <LayoutDashboard className="text-white" size={24} />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl font-bold text-slate-900 leading-none">GestãoPro</h1>
            <p className="text-xs text-[#1b5e20] font-medium mt-1 uppercase tracking-wider">v2.2 Unificada</p>
          </div>
        </div>
        <button 
          className="lg:hidden p-2 hover:bg-slate-100 rounded-lg transition-colors shrink-0" 
          onClick={onCloseMobile}
          aria-label="Fechar menu de navegação"
        >
          <X />
        </button>
      </div>

      <nav className={`flex-1 px-4 mt-4 space-y-1 overflow-y-auto ${isCollapsed ? 'lg:hidden' : ''}`}>
        <NavItem 
          active={currentView === 'DASHBOARD'} 
          onClick={() => go('DASHBOARD')} 
          icon={<LayoutDashboard size={20} />} 
          label="Dashboard" 
        />
        <NavItem 
          active={currentView === 'MOVEMENTS'} 
          onClick={() => go('MOVEMENTS')} 
          icon={<PlusCircle size={20} />} 
          label="Movimentos" 
        />
        <NavItem 
          active={currentView === 'OPERATIONS'} 
          onClick={() => go('OPERATIONS')} 
          icon={<MapIcon size={20} />} 
          label="Tarefas" 
        />
        <NavItem 
          active={currentView === 'TECH'} 
          onClick={() => go('TECH')} 
          icon={<Wrench size={20} />} 
          label="Assistências" 
        />
        <NavItem 
          active={currentView === 'CLIENTS'} 
          onClick={() => go('CLIENTS')} 
          icon={<Users size={20} />} 
          label="Clientes" 
        />
        {isEmployee && (
          <NavItem 
            active={currentView === 'CURSO'} 
            onClick={() => go('CURSO')} 
            icon={<GraduationCap size={20} />} 
            label="Curso" 
          />
        )}
        {isOwnerOrAdmin && (
          <>
            <div className="pt-4 pb-2 px-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
              GESTÃO
            </div>
            <NavItem 
              active={currentView === 'REPORTS'} 
              onClick={() => go('REPORTS')} 
              icon={<BarChart3 size={20} />} 
              label="Relatórios" 
            />
            <NavItem 
              active={currentView === 'CASH'} 
              onClick={() => go('CASH')} 
              icon={<Wallet size={20} />} 
              label="Caixa" 
            />
            <NavItem 
              active={currentView === 'USERS'} 
              onClick={() => go('USERS')} 
              icon={<Users size={20} />} 
              label="Equipa" 
            />
          </>
        )}
        
        {/* Pro-only: registos AC/Painel Solar (não é a página pública de painéis solares do site) */}
        <div className="pt-4 pb-2 px-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
          AC/ PAINEL SOLAR
        </div>
        <NavItem 
          active={currentView === 'AC_PANEL_SOLAR'} 
          onClick={() => go('AC_PANEL_SOLAR')} 
          icon={<Sun size={20} />} 
          label="AC/ Painel solar" 
        />
      </nav>

      <div className={`p-4 border-t border-slate-200 ${isCollapsed ? 'lg:hidden' : ''}`}>
        <div className="bg-white p-4 rounded-2xl flex items-center gap-3">
          <div className="w-10 h-10 bg-[#1b5e20] text-white rounded-full flex items-center justify-center font-bold">
            {currentUser.name[0]}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-slate-900 truncate">{currentUser.name}</p>
            <p className="text-[10px] text-slate-500 uppercase">{currentUser.role}</p>
          </div>
          <button 
            onClick={onLogout} 
            className="text-slate-400 hover:text-red-500 p-2 hover:bg-slate-100 rounded-lg transition-colors"
            aria-label="Terminar sessão"
          >
            <LogOut size={18} />
          </button>
        </div>
      </div>
    </aside>
  );
}

